import React from "react";
import styled, { css } from "styled-components";

/**
 * 吹き出し (Speech bubble) の汎用コンポーネント。
 *
 * - 常時表示。`ToolTip` (Tippy ベース・ホバー出現) とは責務を分離する用途で使う。
 * - 用途例: オファー作成のユーザー選択詳細に「忙しさ」を吹き出しとして添える等。
 *
 * 見た目は短いラベルを乗せる pill 形状に固定:
 *  - フォント: `.label8` (8px / `--soremo-light` 系)
 *  - 角丸: 完全な pill（`border-radius: 9999px`）
 *  - 余白: `4px 10px`
 *
 * tail (尻尾) は CSS の擬似要素で描画する。`direction` で向きを、`tailOffset`
 * で本体上のどこから生やすかを制御する。
 */

export type SpeechBubbleDirection = "top" | "bottom" | "left" | "right";
export type SpeechBubbleTailOffset = "start" | "center" | "end";
export type SpeechBubbleVariant = "default" | "muted";

export interface SpeechBubbleProps {
  children: React.ReactNode;
  /**
   * 吹き出しの tail が指す向き。たとえば `direction="bottom"` のとき、
   * tail は吹き出しの下端から下方向に伸び、「真下にある要素を指す」見た目になる。
   * 既定は `bottom`。
   */
  direction?: SpeechBubbleDirection;
  /**
   * tail の本体上の位置 (`start` / `center` / `end`)。
   * - top / bottom のとき: 左寄り / 中央 / 右寄り
   * - left / right のとき: 上寄り / 中央 / 下寄り
   * 既定は `center`。
   */
  tailOffset?: SpeechBubbleTailOffset;
  /**
   * 色のバリエーション。
   * - `default`: 白背景 + ボーダー (`--soremo-border`)
   * - `muted`: 控えめなグレー背景 (`--soremo-background`)
   */
  variant?: SpeechBubbleVariant;
  className?: string;
  role?: string;
  "aria-label"?: string;
}

/**
 * tail のスタイル。本体 (`Bubble`) からはみ出した三角を `::before` (ボーダー色) と
 * `::after` (背景色) の 2 つの擬似要素で重ね合わせて表現する。これにより 1px
 * ボーダーを保ちつつ tail もボーダー色で縁取られる見た目になる。
 */
const TAIL_SIZE = 8; // 三角の一辺サイズ (px)。

const directionStyles = (
  direction: SpeechBubbleDirection,
  offset: SpeechBubbleTailOffset,
) => {
  const alignAxis = (() => {
    switch (offset) {
      case "start":
        return css`
          ${direction === "top" || direction === "bottom" ? "left: 16px;" : "top: 16px;"}
        `;
      case "end":
        return css`
          ${direction === "top" || direction === "bottom" ? "right: 16px;" : "bottom: 16px;"}
        `;
      case "center":
      default:
        return css`
          ${direction === "top" || direction === "bottom"
            ? "left: 50%; transform: translateX(-50%);"
            : "top: 50%; transform: translateY(-50%);"}
        `;
    }
  })();

  switch (direction) {
    case "top":
      // 吹き出し本体の上端から上向きに tail。
      return css`
        &::before,
        &::after {
          ${alignAxis}
          bottom: 100%;
          border-left: ${TAIL_SIZE}px solid transparent;
          border-right: ${TAIL_SIZE}px solid transparent;
        }
        &::before {
          border-bottom: ${TAIL_SIZE}px solid var(--soremo-border);
        }
        &::after {
          margin-bottom: -1px;
          border-bottom: ${TAIL_SIZE}px solid var(--bubble-bg);
        }
      `;
    case "left":
      return css`
        &::before,
        &::after {
          ${alignAxis}
          right: 100%;
          border-top: ${TAIL_SIZE}px solid transparent;
          border-bottom: ${TAIL_SIZE}px solid transparent;
        }
        &::before {
          border-right: ${TAIL_SIZE}px solid var(--soremo-border);
        }
        &::after {
          margin-right: -1px;
          border-right: ${TAIL_SIZE}px solid var(--bubble-bg);
        }
      `;
    case "right":
      return css`
        &::before,
        &::after {
          ${alignAxis}
          left: 100%;
          border-top: ${TAIL_SIZE}px solid transparent;
          border-bottom: ${TAIL_SIZE}px solid transparent;
        }
        &::before {
          border-left: ${TAIL_SIZE}px solid var(--soremo-border);
        }
        &::after {
          margin-left: -1px;
          border-left: ${TAIL_SIZE}px solid var(--bubble-bg);
        }
      `;
    case "bottom":
    default:
      return css`
        &::before,
        &::after {
          ${alignAxis}
          top: 100%;
          border-left: ${TAIL_SIZE}px solid transparent;
          border-right: ${TAIL_SIZE}px solid transparent;
        }
        &::before {
          border-top: ${TAIL_SIZE}px solid var(--soremo-border);
        }
        &::after {
          margin-top: -1px;
          border-top: ${TAIL_SIZE}px solid var(--bubble-bg);
        }
      `;
  }
};

const Bubble = styled.div.attrs({
  className: "label8",
})<{
  $direction: SpeechBubbleDirection;
  $offset: SpeechBubbleTailOffset;
  $variant: SpeechBubbleVariant;
}>`
  --bubble-bg: ${({ $variant }) =>
    $variant === "muted" ? "var(--soremo-background)" : "#ffffff"};
  position: relative;
  display: inline-block;
  max-width: 100%;
  padding: 4px 10px;
  background: var(--bubble-bg);
  border: 1px solid var(--soremo-border);
  /* 完全な pill 形状にするため、高さの半分を十分に超える大きな値を指定する。
     CSS の border-radius は要素の最小辺の半分でクランプされるため 9999px でも安全。 */
  border-radius: 9999px;
  color: var(--soremo-text, inherit);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    pointer-events: none;
  }

  ${({ $direction, $offset }) => directionStyles($direction, $offset)}
`;

/**
 * 常時表示型の吹き出し UI。中身 (`children`) は任意の React ノードを受ける。
 *
 * NOTE: `role` を明示的に渡せるようにしてあり、アクセシビリティ上のセマンティクス
 * (例: `status` / `tooltip`) は呼び出し側が責任を持って指定する。
 */
const SpeechBubble: React.FC<SpeechBubbleProps> = ({
  children,
  direction = "bottom",
  tailOffset = "center",
  variant = "default",
  className,
  role,
  "aria-label": ariaLabel,
}) => {
  return (
    <Bubble
      $direction={direction}
      $offset={tailOffset}
      $variant={variant}
      className={className}
      role={role}
      aria-label={ariaLabel}
    >
      {children}
    </Bubble>
  );
};

export default SpeechBubble;
